// Summary view for reconciliation screens (account card, card matching).
//
// Rows that belong to the same PaymentAcceptance (e.g. a receipt paid with 4 checks) or to the
// same JournalGroup (a document and its duplicates/splits) are collapsed into one row per side
// of the card, with the amounts summed. Debit and credit rows are never netted together.
//
// Each collapsed row exposes:
//   _summary  : true
//   _count    : number of collapsed rows
//   _children : the original rows

export const ROW_VIEW_MODES = { FULL: 'full', SUMMARY: 'summary' };

const groupIdOf = (row) => row?.groupId ?? row?.GroupId ?? null;

const sumOf = (rows, key) => rows.reduce((acc, row) => acc + (Number(row?.[key]) || 0), 0);

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

const hasAny = (rows, key) => rows.some((row) => row?.[key] != null);

/**
 * Builds the collapse key for a row, or null when the row stays on its own.
 * A JournalGroup takes precedence over the payment so a grouped payment collapses with its group.
 */
const buildKey = (row, side, paymentAcceptanceId, scope = '') => {
    if (!side) return null;
    const groupId = groupIdOf(row);
    if (groupId) return `grp:${groupId}|${side}|${scope}`;
    if (paymentAcceptanceId) return `pa:${paymentAcceptanceId}|${side}|${scope}`;
    return null;
};

const collapseByKey = (rows, keyOf, merge) => {
    const out = [];
    const indexByKey = new Map();
    for (const row of rows || []) {
        const key = keyOf(row);
        if (!key) {
            out.push(row);
            continue;
        }
        if (indexByKey.has(key)) {
            out[indexByKey.get(key)]._children.push(row);
            continue;
        }
        indexByKey.set(key, out.length);
        out.push({ _key: key, _children: [row] });
    }
    return out.map((item) => {
        if (!item._key) return item;
        if (item._children.length === 1) return item._children[0];
        return merge(item._children, item._key);
    });
};

// ---------------------------------------------------------------------------------------------
// Account card (AccountCardTransactionDTO rows)
// ---------------------------------------------------------------------------------------------

const accountCardSide = (row) => {
    if ((Number(row?.debitAccount) || 0) !== 0) return 'debit';
    if ((Number(row?.creditAccount) || 0) !== 0) return 'credit';
    return null;
};

const accountCardPaymentId = (row) =>
    row?.type1 === 'PaymentAcceptances' ? (row.id ?? row.ID ?? null) : null;

/**
 * Collapses account card transactions and recomputes the running balance per card.
 * Rows may span several cards (flattened multi-card list); each card keeps its own balance.
 */
export const collapseAccountCardRows = (rows) => {
    if (!rows || rows.length === 0) return rows || [];

    const collapsed = collapseByKey(
        rows,
        (row) => buildKey(row, accountCardSide(row), accountCardPaymentId(row), row?.cardId ?? ''),
        (children) => {
            const first = children[0];
            const merged = {
                ...first,
                debitAccount: round2(sumOf(children, 'debitAccount')),
                creditAccount: round2(sumOf(children, 'creditAccount')),
                _summary: true,
                _count: children.length,
                _children: children,
            };
            if (hasAny(children, 'foreignCurrencyDebit')) merged.foreignCurrencyDebit = round2(sumOf(children, 'foreignCurrencyDebit'));
            if (hasAny(children, 'foreignCurrencyCredit')) merged.foreignCurrencyCredit = round2(sumOf(children, 'foreignCurrencyCredit'));
            return merged;
        }
    );

    // Opening balance of each card = first row's balance before that row's own movement.
    const running = new Map();
    const runningFc = new Map();
    const firstSeen = new Set();
    for (const row of rows) {
        const card = row?.cardId ?? '';
        if (firstSeen.has(card)) continue;
        firstSeen.add(card);
        running.set(card, (Number(row.balance) || 0) - (Number(row.debitAccount) || 0) + (Number(row.creditAccount) || 0));
        if (row.foreignCurrencyBalance != null) {
            runningFc.set(card, (Number(row.foreignCurrencyBalance) || 0)
                - (Number(row.foreignCurrencyDebit) || 0) + (Number(row.foreignCurrencyCredit) || 0));
        }
    }

    return collapsed.map((row) => {
        const card = row?.cardId ?? '';
        const balance = round2((running.get(card) || 0) + (Number(row.debitAccount) || 0) - (Number(row.creditAccount) || 0));
        running.set(card, balance);
        const next = { ...row, balance };
        if (runningFc.has(card)) {
            const fcBalance = round2(runningFc.get(card) + (Number(row.foreignCurrencyDebit) || 0) - (Number(row.foreignCurrencyCredit) || 0));
            runningFc.set(card, fcBalance);
            next.foreignCurrencyBalance = fcBalance;
        }
        return next;
    });
};

// ---------------------------------------------------------------------------------------------
// Card matching rows
// ---------------------------------------------------------------------------------------------

/**
 * Collapses card matching rows of one panel (debit or credit). Matched rows only collapse within
 * the same match number. Children are attached as `subRows` (flagged `_summaryChild`) so the
 * grid can expand them.
 * @param {Array} rows
 * @param {(amount:number) => string} formatAmount - formatter for the display `amount` column.
 */
export const collapseCardMatchingRows = (rows, formatAmount = (v) => v) => {
    if (!rows || rows.length === 0) return rows || [];

    return collapseByKey(
        rows,
        (row) => buildKey(row, row?.side || 'panel', row?.paymentAcceptanceId, row?.cardMatchNumber ?? ''),
        (children, key) => {
            const first = children[0];
            const amount1 = round2(sumOf(children, 'amount1'));
            const merged = {
                ...first,
                id: `sum:${key}`,
                amount1,
                amount: formatAmount(amount1),
                shekelAmount1: round2(sumOf(children, 'shekelAmount1')),
                subRows: children.map((child) => ({ ...child, _summaryChild: true })),
                _summary: true,
                _count: children.length,
                _children: children,
            };
            if (hasAny(children, 'foreignCurrencyAmount')) {
                merged.foreignCurrencyAmount = round2(sumOf(children, 'foreignCurrencyAmount'));
            }
            return merged;
        }
    );
};

/** True for a synthetic summary row id produced by collapseCardMatchingRows. */
export const isSummaryRowId = (id) => typeof id === 'string' && id.startsWith('sum:');

/**
 * Turns selected grid ids into the real `{ id, type }` transactions behind them. A summary row
 * expands to all of its children; ids that are not in `rows` (e.g. a just-created journal entry)
 * are kept with `defaultType`. Duplicates are removed.
 */
export const expandSelectedIds = (ids, rows, defaultType = 'JournalEntries') => {
    const byId = new Map((rows || []).map((row) => [row.id, row]));
    const seen = new Set();
    const out = [];
    const push = (id, type) => {
        const key = `${id}|${type}`;
        if (seen.has(key)) return;
        seen.add(key);
        out.push({ id, type });
    };
    for (const id of ids || []) {
        const row = byId.get(id);
        if (row?._summary) {
            row._children.forEach((child) => push(child.id, child.type || defaultType));
        } else if (!isSummaryRowId(id)) {
            push(id, row?.type || defaultType);
        }
    }
    return out;
};
